import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { TrendUp, ArrowRight, ChartLineUp, Handshake, ListDashes, Receipt } from "@phosphor-icons/react";
import { CountUp } from "@/components/motion/Reveal";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function Overview() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, n] = await Promise.all([
          api.get("/users/me/dashboard-stats"),
          api.get("/deals"),
          api.get("/notifications"),
        ]);
        setStats(s.data);
        setDeals(d.data);
        setNotes(n.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="overview-page">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats
          ? stats.kpis.map((k, i) => (
              <motion.div
                key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="lift rounded-xl bg-white border border-stone-200 p-5"
                data-testid={`kpi-card-${i}`}
              >
                <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{k.label}</div>
                <div className="mt-2 font-display text-3xl font-black text-emerald-950">
                  {k.format === "currency" ? "₹" : ""}
                  <CountUp value={k.value || 0} format={k.format} />
                </div>
                {k.delta && (
                  <div className={`mt-1 text-xs font-semibold flex items-center gap-1 ${k.trend === "up" ? "text-emerald-700" : "text-red-600"}`}>
                    <TrendUp size={12} weight="bold" /> {k.delta}
                  </div>
                )}
              </motion.div>
            ))
          : Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-white border border-stone-200 p-5 h-28 shimmer" />
            ))}
      </div>

      {/* Content grid */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-xl bg-white border border-stone-200 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-stone-200">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Latest Deals</div>
              <div className="font-display text-lg font-bold text-stone-900">Track every deal, every stage</div>
            </div>
            <Link to="/app/deals" className="text-sm font-semibold text-emerald-800 hover:text-orange-600 flex items-center gap-1" data-testid="view-all-deals">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-widest font-bold">
              <tr>
                <th className="text-left p-3">Commodity</th>
                <th className="text-left p-3">Counterparty</th>
                <th className="text-right p-3">Value</th>
                <th className="text-center p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {deals.length === 0 && (
                <tr><td colSpan={4} className="p-10 text-center text-stone-500">No deals yet. <Link to="/app/listings" className="text-emerald-800 font-semibold">Create a listing →</Link></td></tr>
              )}
              {deals.slice(0, 6).map((d) => {
                const isBuyer = d.buyer_id === user.id;
                const cp = isBuyer ? d.seller_name : d.buyer_name;
                return (
                  <tr key={d.id} className="border-t border-stone-100 hover:bg-stone-50 transition-colors">
                    <td className="p-3 font-semibold text-stone-800">{d.commodity} · {d.quantity} {d.unit}</td>
                    <td className="p-3 text-stone-600">{cp}</td>
                    <td className="p-3 text-right font-mono font-semibold">₹{(d.total_value || 0).toLocaleString("en-IN")}</td>
                    <td className="p-3 text-center"><StatusBadge status={d.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <div className="rounded-xl bg-emerald-950 text-white p-5">
            <div className="text-xs uppercase tracking-widest font-bold text-orange-400">Quick Actions</div>
            <div className="mt-3 space-y-2">
              <QA icon={ListDashes} to="/app/listings?create=1" label="Create Listing" testid="qa-create-listing" />
              <QA icon={Handshake} to="/app/deals" label="View My Deals" testid="qa-view-deals" />
              <QA icon={ChartLineUp} to="/app/rates" label="Check Aaj Ka Bhav" testid="qa-check-rates" />
              <QA icon={Receipt} to="/app/invoices" label="Invoices & Billing" testid="qa-invoices" />
            </div>
          </div>

          <div className="rounded-xl bg-white border border-stone-200 p-5">
            <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Recent Notifications</div>
            <div className="mt-3 space-y-3">
              {notes.slice(0, 4).map((n) => (
                <div key={n.id} className="text-sm">
                  <div className="font-semibold text-stone-800">{n.title}</div>
                  <div className="text-xs text-stone-500 line-clamp-2">{n.message}</div>
                </div>
              ))}
              {notes.length === 0 && <div className="text-sm text-stone-500">All caught up ✓</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QA({ icon: Icon, to, label, testid }) {
  return (
    <Link to={to} className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-emerald-900/60 hover:bg-emerald-800 text-white text-sm font-semibold transition-colors" data-testid={testid}>
      <Icon weight="duotone" size={18} /> {label}
    </Link>
  );
}

export function StatusBadge({ status }) {
  const map = {
    matched: "bg-blue-100 text-blue-800 border-blue-200",
    payment_secured: "bg-emerald-100 text-emerald-800 border-emerald-200",
    in_transport: "bg-orange-100 text-orange-800 border-orange-200",
    delivered: "bg-stone-200 text-stone-800 border-stone-300",
    cancelled: "bg-red-100 text-red-800 border-red-200",
    disputed: "bg-red-100 text-red-800 border-red-200",
  };
  const label = (status || "").replace(/_/g, " ");
  return <span className={`inline-block px-2 py-1 rounded border text-[11px] font-bold uppercase tracking-wide ${map[status] || "bg-stone-100 text-stone-700 border-stone-200"}`}>{label}</span>;
}
